// @flow
import React, { PureComponent } from 'react';
import MentionFlyout from 'app/common/react/components/Mentions/MentionFlyout';
import { Box } from 'gestalt';
import {
  createMentionSpan,
  parseTags,
  LENGTH_DATA_ATTR,
  TAG,
} from 'app/common/react/components/Mentions/common';

const AT_MENTION_REGEX = /@[a-zA-Z0-9_-]*/;
const GLOBAL_FILTER = 'g';
const MAX_CHAR_LENGTH = 2000;
const IGNORED_KEYS = [
  'ArrowDown',
  'ArrowUp',
  'ArrowLeft',
  'ArrowRight',
  'Escape',
  'Shift',
  'Alt',
  'Control',
  'Meta',
];
const QUERY_INDEX = 0;
const ENTER_KEY_VALUE = 13;

type MentionsInputProps = {
  existingTags: Array<Object>,
  handleBlur?: Function,
  handleEnter?: Function,
  handleFocus: Function,
  handleInput?: (inputText: string) => void,
  onChange?: (textChanged: boolean) => void,
  replyToObject?: {
    user: Object,
  },
  setRef: Function,
  shouldLoseFocus?: boolean,
  style: Object,
  suggestionAnchor: ?HTMLElement,
  taggedUsers: Array<Object>,
  textValue?: string,
  updateTags: Function,
};

type MentionsInputState = {
  flyoutCursorIndex: number,
  isMentionFlyoutOpen: boolean,
  mentionQuery: string,
  textValue: string,
};

export default class MentionsInput extends PureComponent<MentionsInputProps, MentionsInputState> {
  static defaultProps = {
    handleFocus: () => {},
    existingTags: [],
    taggedUsers: [],
  };

  state: MentionsInputState = {
    flyoutCursorIndex: 0,
    isMentionFlyoutOpen: false,
    mentionQuery: '',
    textValue: this.props.replyToObject
      ? parseTags(
          this.props.existingTags,
          this.props.taggedUsers,
          this.props.textValue,
          this.props.replyToObject,
        )
      : parseTags(this.props.existingTags, this.props.taggedUsers, this.props.textValue),
  };

  componentDidMount() {
    this.focusTextAreaAndPostionCursor(true);
  }

  componentDidUpdate(prevProps: MentionsInputProps, prevState: MentionsInputState) {
    if (this.state.isMentionFlyoutOpen && !this.props.shouldLoseFocus) {
      this.focusTextAreaAndPostionCursor();
    }
  }

  getTextCursorPosition = (): Object => {
    const selection = window.getSelection();
    const range = selection.getRangeAt(0);
    const selectedElement = selection.anchorNode;
    return { cursorIndex: range.startOffset, selectedElement };
  };

  getHTMLCursorPosition = (): Object => {
    const { cursorIndex, selectedElement } = this.getTextCursorPosition();
    if (selectedElement) {
      let position = cursorIndex;
      // selectedElement will be a text node, so we get the wrapping parentElement
      const parentElement = selectedElement.parentNode;
      // get length of opening tag of parent
      if (parentElement !== this._textAreaRef) {
        position +=
          parentElement.outerHTML.length -
          parentElement.innerHTML.length -
          (3 + parentElement.tagName.length);
      }
      // get length of all siblings that came before this one (♫ we are family ♫)
      let previousSibling = selectedElement.previousSibling;
      while (previousSibling) {
        position +=
          (previousSibling.outerHTML && previousSibling.outerHTML.length) ||
          previousSibling.textContent.length;
        previousSibling = previousSibling.previousSibling;
      }
      return { cursorIndex: position, parentElement };
    }
    return { cursorIndex: -1 };
  };

  setRef = (div: ?HTMLElement) => {
    this.props.setRef(div);
    this._textAreaRef = div;
  };

  _textAreaRef: ?HTMLElement;

  focusTextAreaAndPostionCursor = (isInital: boolean = false) => {
    const selection = window.getSelection();
    const range = document.createRange();
    if (isInital && this._textAreaRef) {
      range.selectNodeContents(this._textAreaRef);
      range.collapse(false);
    } else if (selection.anchorNode) {
      const currentRange = selection.getRangeAt(0);
      const cursorIndex = currentRange.startOffset;
      const selectedElement = currentRange.startContainer;
      range.setStart(selectedElement, cursorIndex);
      range.collapse(true);
      if (this._textAreaRef && selectedElement === this._textAreaRef) {
        range.selectNodeContents(this._textAreaRef);
        range.collapse(false);
      }
    } else if (this._textAreaRef) {
      range.selectNodeContents(this._textAreaRef);
      range.collapse(false);
    }
    selection.removeAllRanges();
    selection.addRange(range);
    if (this._textAreaRef) {
      this._textAreaRef.focus();
    }
  };

  handleKeyDown = (e: Object) => {
    const { handleEnter } = this.props;
    if (!this.state.isMentionFlyoutOpen && e.which === ENTER_KEY_VALUE && handleEnter) {
      e.preventDefault();
      handleEnter(e.target.textContent);
    }
  };

  handleKeyUp = (e: Object) => {
    const { parentElement } = this.getHTMLCursorPosition();
    const { cursorIndex } = this.getTextCursorPosition();
    const atEnd = cursorIndex === parentElement.textContent.length;
    const isTag = parentElement.className === TAG;
    const shortenedName =
      isTag && parentElement.getAttribute(LENGTH_DATA_ATTR) > parentElement.textContent.length;
    if (shortenedName) {
      this.handleRemoveMention(parentElement);
    } else if (isTag && e.which !== 0 && !IGNORED_KEYS.includes(e.key) && !atEnd) {
      this.handleRemoveSpan(parentElement);
    } else if (parentElement.className === TAG && atEnd) {
      this.placeCaretAfterNode(parentElement);
    }
    const currentText = e.target.textContent;

    if (this.props.onChange) {
      this.props.onChange(currentText !== this.props.textValue);
    }
  };

  handleAddMention = (user: Object) => {
    const startIndex = this.state.flyoutCursorIndex;
    // add one here since we take the @ off the mention query
    const endIndex = startIndex + this.state.mentionQuery.length + 1;
    const content = this._textAreaRef;
    const preInsert = (content && content.innerHTML.slice(0, startIndex)) || '';
    const postInsert = (content && content.innerHTML.slice(endIndex)) || ' ';
    const mention = createMentionSpan(user);
    const newText = `${preInsert}${mention.outerHTML}${postInsert}`;
    this.props.updateTags(user);
    this.setState(
      {
        textValue: newText,
        mentionQuery: '',
        isMentionFlyoutOpen: false,
      },
      this.focusTextAreaAndPostionCursor,
    );
  };

  handleRemoveMention = (node: any) => {
    if (this._textAreaRef) {
      this._textAreaRef.replaceChild(document.createTextNode(''), node);
    }
  };

  handleRemoveSpan = (node: any) => {
    if (this._textAreaRef) {
      this._textAreaRef.replaceChild(document.createTextNode(node.textContent), node);
      this.setState(
        {
          textValue: (this._textAreaRef && this._textAreaRef.innerHTML) || '',
        },
        this.focusTextAreaAndPostionCursor,
      );
    }
  };

  handleInput = (e: Object) => {
    const { cursorIndex } = this.getHTMLCursorPosition();
    let activeQuery = '';
    const HTMLValue = e.target.innerHTML;
    const globalMentionRegex = new RegExp(AT_MENTION_REGEX, GLOBAL_FILTER);
    let possibleQuery = globalMentionRegex.exec(HTMLValue);
    let queryStartBeforeCursor = globalMentionRegex.lastIndex < cursorIndex;
    while (!!possibleQuery && queryStartBeforeCursor) {
      possibleQuery = globalMentionRegex.exec(HTMLValue);
      queryStartBeforeCursor = globalMentionRegex.lastIndex < cursorIndex;
    }
    activeQuery =
      !!possibleQuery && possibleQuery.index < cursorIndex ? possibleQuery[QUERY_INDEX] : '';
    const flyoutCursorIndex = this.state.isMentionFlyoutOpen
      ? this.state.flyoutCursorIndex
      : HTMLValue.slice(0, cursorIndex).lastIndexOf('@');
    const mentionQuery = activeQuery.substring(1);
    this.setState({
      flyoutCursorIndex,
      mentionQuery,
      isMentionFlyoutOpen: !!activeQuery,
    });

    if (this.props.handleInput) {
      this.props.handleInput(e.target.textContent);
    }
  };

  handlePaste = (e: Object) => {
    e.preventDefault();
    const pastedText = e.clipboardData.getData('text/plain');
    document.execCommand('insertHTML', false, pastedText);
  };

  placeCaretAfterNode = (node: any) => {
    const content = node.textContent;
    const originalLength = node.getAttribute(LENGTH_DATA_ATTR);
    const excess = content.substring(originalLength);
    const originalTag = content.substring(0, originalLength);
    node.innerHTML = originalTag;
    const range = document.createRange();
    range.setStartAfter(node);
    const textNode = document.createTextNode(excess);
    range.insertNode(textNode);
    range.collapse(false);
    const selection = window.getSelection();
    selection.removeAllRanges();
    selection.addRange(range);
  };

  render() {
    const { style, suggestionAnchor, handleFocus } = this.props;
    const { isMentionFlyoutOpen, mentionQuery, textValue } = this.state;
    return (
      <Box alignItems="center" width="100%">
        <div
          contentEditable
          data-test-id="mentionsInput"
          maxLength={MAX_CHAR_LENGTH}
          name="communityItemTextBox"
          onBlur={this.props.handleBlur}
          onFocus={handleFocus}
          onInput={this.handleInput}
          onKeyDown={this.handleKeyDown}
          onKeyUp={this.handleKeyUp}
          onPaste={this.handlePaste}
          ref={this.setRef}
          role="textbox"
          style={style}
          tabIndex="0"
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{ __html: textValue }}
        />
        {suggestionAnchor &&
          isMentionFlyoutOpen && (
            <MentionFlyout
              addMention={this.handleAddMention}
              anchor={suggestionAnchor}
              query={mentionQuery}
            />
          )}
      </Box>
    );
  }
}
